import React, { useState, useEffect } from 'react';
import { HiCheckCircle, HiOutlineChatBubbleLeftRight, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';

export default function GeneratorRental() {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', companyName: '', siteLocation: '', capacity: '', duration: '', message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleApplyClick = (usecase) => {
    setFormData(prev => ({ ...prev, message: `Inquiry regarding rentals for: ${usecase}` }));
    const formElement = document.getElementById('rental-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const nameInput = document.getElementById('client-name');
      if (nameInput) nameInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      companyName: formData.companyName || '',
      siteLocation: formData.siteLocation || '',
      productId: 'rental',
      productName: `Generator Rental (${formData.capacity})`,
      city: formData.siteLocation || '',
      message: formData.message || `Rental request for a ${formData.capacity} generator for a duration of ${formData.duration}. Company: ${formData.companyName}`,
      type: 'rental',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Rental Enquiry Received!',
        text: `Thank you, ${formData.name}. We have received your rental request for a ${formData.capacity} generator for a duration of ${formData.duration}. Our rental dispatch coordinators will contact you shortly.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({ name: '', phone: '', email: '', companyName: '', siteLocation: '', capacity: '', duration: '', message: '' });
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  const useCases = [
    { title: 'Factories & Manufacturing', desc: 'Ensure continuous line operations during seasonal grid shortages or scheduled outages. Rent high kVA heavy units with optional synchronization panels.', badge: 'Continuous Duty' },
    { title: 'Industrial Sites', desc: 'Robust power backups backing heavy construction, road and bridge building, MIDC site projects, and metro construction layouts.', badge: 'Heavy Backup' },
    { title: 'Events & Temporary Power', desc: 'Super silent generators designed for corporate gatherings, product launches, exhibitions, and massive temporary commercial setups.', badge: 'Zero Noise' },
  ];

  const rentalAdvantages = [
    'No capital expenditure required', 'Immediate deployment within 24 hours', 'Full operator support included',
    'Flexible short-term & long-term plans', 'All maintenance included during rental', 'GPS-monitored fleet for SLA compliance',
  ];

  const capacityTiers = [
    { range: '20 – 62.5 kVA', use: 'Small retail, petrol pumps, farmhouses', price: 'Contact for rates' },
    { range: '82.5 – 250 kVA', use: 'Hospitals, hotels, mid-scale factories', price: 'Contact for rates' },
    { range: '320 – 750 kVA', use: 'Large industrial plants, IT parks', price: 'Contact for rates' },
    { range: '1000 – 1500 kVA', use: 'Mega infrastructure & event setups', price: 'Contact for rates' },
  ];

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Generator Solutions.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Flexible Leases</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Commercial-Grade <span className="text-gradient-gold">Generator Rental</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Immediate dispatch rental fleet ranging from 20 kVA to 1500 kVA. Powering factories, infrastructure projects, and events without high upfront purchase costs.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span>Generator Solutions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Rental</span>
        </div>
      </div>

      {/* Use Cases */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow">Rental Applications</span>
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading">Who Benefits From Genset Rentals?</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {useCases.map((uc, i) => (
            <div key={i} className="bg-white border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 group flex flex-col">
              <div className="flex items-start justify-between mb-5">
                <div className="w-10 h-10 rounded-xl card-icon font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <span className="badge-gold text-[9px]">{uc.badge}</span>
              </div>
              <h3 className="text-sm font-display font-extrabold text-heading mb-3 group-hover:text-primary transition-colors">{uc.title}</h3>
              <p className="text-xs text-body leading-relaxed flex-grow mb-5">{uc.desc}</p>
              <button
                onClick={() => handleApplyClick(uc.title)}
                className="btn-secondary text-xs py-2.5 rounded-xl"
              >
                Enquire This Application
                <HiArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Capacity Table */}
        <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card mb-20">
          <div className="px-6 py-5 bg-primary border-b border-primary-dark">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider">Rental Capacity Tiers</h3>
          </div>
          <table className="w-full">
            <thead className="bg-light-section">
              <tr>
                <th className="p-4 text-left text-xs font-bold text-heading uppercase tracking-wide">Capacity Range</th>
                <th className="p-4 text-left text-xs font-bold text-heading uppercase tracking-wide">Typical Applications</th>
                <th className="p-4 text-left text-xs font-bold text-heading uppercase tracking-wide">Pricing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {capacityTiers.map((tier, i) => (
                <tr key={i} className="hover:bg-light-section transition-colors">
                  <td className="p-4 font-bold text-accent text-sm">{tier.range}</td>
                  <td className="p-4 text-sm text-body">{tier.use}</td>
                  <td className="p-4">
                    <button onClick={() => handleApplyClick(tier.use)} className="text-xs font-bold text-primary hover:text-accent transition-colors flex items-center gap-1">
                      {tier.price} <HiArrowUpRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Advantages */}
        <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
          <div className="relative z-10">
            <span className="eyebrow">Why Rent</span>
            <h2 className="text-2xl font-display font-extrabold text-white mb-8">Rental Advantages</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {rentalAdvantages.map((adv, i) => (
                <div key={i} className="flex items-center gap-3">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-sm text-white/85 font-medium">{adv}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rental Enquiry Form */}
      <section id="rental-form" className="py-24 bg-light-section border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="eyebrow">Instant Rental</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Request a Generator Rental</h2>
            <p className="text-sm text-body-muted max-w-xl mx-auto mt-2">
              Share your site details and our dispatch team will respond with a quote within 2 hours.
            </p>
          </div>

          <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                <span className="text-xs font-bold text-accent uppercase tracking-widest">Rental Enquiry Form</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                 {/* Row 1: Name, Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                    <input id="client-name" type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Contact Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="e.g. 9876543210" className="input-dark" />
                  </div>
                </div>

                {/* Row 2: Email, Company */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@company.com" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Company Name</label>
                    <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required placeholder="Company or organisation name" className="input-dark" />
                  </div>
                </div>

                {/* Row 2: Site Location, Capacity, Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Site Location / City</label>
                    <input type="text" name="siteLocation" value={formData.siteLocation} onChange={handleChange} required placeholder="e.g. Pune, MIDC Chakan" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Required Capacity (kVA)</label>
                    <input type="text" name="capacity" value={formData.capacity} onChange={handleChange} required placeholder="e.g. 250 kVA" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Rental Duration</label>
                    <select name="duration" value={formData.duration} onChange={handleChange} required className="input-dark bg-charcoal text-white/85 appearance-none">
                      <option value="" className="bg-white">Select duration</option>
                      <option value="1-7 Days" className="bg-white">1–7 Days</option>
                      <option value="1-4 Weeks" className="bg-white">1–4 Weeks</option>
                      <option value="1-6 Months" className="bg-white">1–6 Months</option>
                      <option value="6+ Months (Long Term)" className="bg-white">6+ Months (Long Term)</option>
                    </select>
                  </div>
                </div>

                {/* Row 3: Message + Submit side by side */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <div className="md:col-span-8">
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message / Additional Details</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={2} required placeholder="Describe your power requirement and site conditions..." className="input-dark resize-none" />
                  </div>
                  <div className="md:col-span-4">
                    <button type="submit" disabled={isSubmitted} className="btn-green w-full justify-center py-3.5 rounded-xl text-sm disabled:opacity-60 font-bold">
                      Submit Rental Enquiry
                      <HiArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
